// Umbenennen und Beenden einer Sammlung (ST-SEA-002 Plan F3, Schnitt 3b-1;
// ADR-0065 Ziffer 2; Bauvorgabe DESIGN "Sammlung 3b", Abschnitt 2).
//
// BEENDEN IST ENDGUELTIG: Der Dialog verspricht keine Wiederherstellung. Die
// Mitglieder bleiben zwar 90 Tage gehalten, aber es gibt keinen Weg dorthin.
//
// VERALTET (lockVersion): Der Dialog bleibt offen und behaelt die Eingabe; die
// Ansicht laedt im Hintergrund neu, der naechste Versuch traegt den neuen Stand.
//
// ABBRUCH OHNE ANTWORT: Der Dialog liest die Sammlung und vergleicht mit der
// Absicht. Gilt sie schon, ist es ein Erfolg; sonst "nicht gespeichert" mit
// frischem Stand. "Offen" bleibt nur, wenn auch das Lesen scheitert.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Body1, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles } from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import {
  changeUncertainMessage,
  endCollection,
  fetchCollection,
  nameErrorSentence,
  notSavedAfterAbortMessage,
  nameProblem,
  outdatedMessage,
  RENAME_NAME_TAKEN,
  renameCollection,
  type CollectionSummary,
} from '../collections.ts';
import { Meldung, NamensFeld, type Zugang } from './zur-sammlung.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const NAME_ID = 'sammlung-umbenennen-name';

/** Der Stand nach einem Abbruch: die Sammlung, `weg` (beendet oder fremd: dasselbe 404) oder unbekannt. */
async function abgleichen(zugang: Zugang, collectionId: string): Promise<CollectionSummary | 'weg' | undefined> {
  try {
    const token = await zugang.accessToken();
    if (token === undefined) {
      // Abgelaufen, waehrend die Antwort ausblieb: neu anmelden wie bei jedem anderen Aufruf.
      zugang.onReauthenticate();
      return undefined;
    }
    const result = await fetchCollection({ baseUrl: zugang.apiBaseUrl, accessToken: token, collectionId });
    return result.outcome === 'loaded' ? result.collection : result.outcome === 'gone' ? 'weg' : undefined;
  } catch {
    return undefined;
  }
}

/** Der Server lehnte ab, ohne dass ein eigener Satz passt -- anders als "nicht erreichbar". */
const NICHT_GESPEICHERT: PlainMessage = Object.freeze({
  headline: 'Das hat nicht geklappt.',
  explanation: 'LIAISON hat die Änderung nicht angenommen.',
  nextStep: 'Lade die Seite neu und versuch es noch einmal.',
  retryable: false,
});

const useStyles = makeStyles({
  inhalt: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  // Ein langer Name ohne Leerzeichen bricht um statt bei 320 px ueberzulaufen.
  titel: { overflowWrap: 'anywhere' },
  aktionen: { flexWrap: 'wrap' },
});

/**
 * Die Ansage der Uebersicht nach dem Beenden. Ein Merker statt eines
 * Adressteils, wie `titelNachAnlageFokussieren`: ein kopierter Link soll
 * nichts ansagen.
 */
let uebersichtAnsage: string | undefined;
export function nachBeendenAnsagen(satz: string): void {
  uebersichtAnsage = satz;
}
/** Liest die Ansage einmal und vergisst sie. */
export function ansageNachBeenden(): string | undefined {
  const satz = uebersichtAnsage;
  uebersichtAnsage = undefined;
  return satz;
}

type Ausgang = 'fertig' | 'weg' | 'veraltet' | 'gescheitert';

/** Der Fokus vor dem Aushaengen weg, sonst bleiben Fluents Fokuswaechter stehen (axe aria-hidden-focus). */
function fokusAbgeben(): void {
  (document.activeElement as HTMLElement | null)?.blur();
}

export function UmbenennenDialog(
  props: Zugang & {
    readonly sammlung: CollectionSummary;
    readonly onSchliessen: () => void;
    readonly onUmbenannt: (sammlung: CollectionSummary) => void;
    readonly onWeg: () => void;
    /** Veraltet: die Ansicht laedt neu, ohne den Dialog auszuhaengen; falsch, wenn das scheitert. */
    readonly onNeuLaden: () => Promise<boolean>;
  },
): ReactElement {
  const own = useStyles();
  const [name, setName] = useState(props.sammlung.collectionName);
  const [feldFehler, setFeldFehler] = useState<string | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Nach einem Abbruch: der Knopf sagt, dass abgeglichen wird.
  const [prueft, setPrueft] = useState(false);
  const abgebrochen = useRef(false);

  // Der Name ist markiert: Tippen ersetzt ihn, Pfeiltasten aendern ihn.
  useEffect(() => {
    const feld = document.getElementById(NAME_ID) as HTMLInputElement | null;
    feld?.focus();
    feld?.select();
  }, []);

  const schliessen = (): void => {
    abgebrochen.current = true;
    fokusAbgeben();
    props.onSchliessen();
  };

  const amFeld = (satz: string): void => {
    setFeldFehler(satz);
    document.getElementById(NAME_ID)?.focus();
  };

  const senden = async (): Promise<void> => {
    if (busy) return;
    const problem = nameProblem(name);
    if (problem !== undefined) {
      amFeld(problem);
      return;
    }
    // Unveraendert: nichts zu speichern.
    if (name.normalize('NFC').trim() === props.sammlung.collectionName) {
      schliessen();
      return;
    }
    setBusy(true);
    setFehler(undefined);
    let ausgang: Ausgang = 'gescheitert';
    try {
      const token = await props.accessToken();
      if (token === undefined) {
        props.onReauthenticate();
        return;
      }
      const result = await renameCollection({
        baseUrl: props.apiBaseUrl,
        accessToken: token,
        collectionId: props.sammlung.collectionId,
        name,
        lockVersion: props.sammlung.lockVersion,
      });
      // Abgebrochen, aber schon gespeichert: die Ansicht zeigt trotzdem den neuen Namen und Stand.
      if (abgebrochen.current) {
        if (result.outcome === 'applied' && result.collection !== undefined) props.onUmbenannt(result.collection);
        return;
      }
      if (result.outcome === 'applied' && result.collection !== undefined) {
        ausgang = 'fertig';
        fokusAbgeben();
        props.onUmbenannt(result.collection);
        return;
      }
      if (result.outcome === 'not-found') {
        ausgang = 'weg';
        fokusAbgeben();
        props.onWeg();
        return;
      }
      if (result.outcome === 'conflict' && result.reasonCode === 'collection_name_taken') {
        amFeld(RENAME_NAME_TAKEN);
        return;
      }
      if (result.outcome === 'invalid') {
        const amNamen = result.errors.find((error) => error.fieldPath === 'name');
        if (amNamen !== undefined) {
          amFeld(nameErrorSentence(amNamen.reasonCode));
          return;
        }
      }
      if (result.outcome === 'conflict' && result.reasonCode === 'party_collection_outdated') {
        // Ein frueherer, abgebrochener Versuch kann angekommen sein: gilt der Name schon, ist es ein Erfolg.
        const stand = await abgleichen(props, props.sammlung.collectionId);
        if (typeof stand === 'object' && stand.collectionName === name.normalize('NFC').trim()) {
          ausgang = 'fertig';
          fokusAbgeben();
          props.onUmbenannt(stand);
          return;
        }
        ausgang = 'veraltet';
        setFehler((await props.onNeuLaden()) ? result.message : outdatedMessage('umbenennen', false));
        return;
      }
      setFehler('message' in result ? result.message : NICHT_GESPEICHERT);
    } catch {
      // Der Knopf bleibt gesperrt, bis der Abgleich steht.
      setPrueft(true);
      const stand = await abgleichen(props, props.sammlung.collectionId);
      const gilt = typeof stand === 'object' && stand.collectionName === name.normalize('NFC').trim();
      if (abgebrochen.current) {
        if (gilt) props.onUmbenannt(stand);
        else if (stand === 'weg') props.onWeg();
        return;
      }
      if (gilt) {
        ausgang = 'fertig';
        fokusAbgeben();
        props.onUmbenannt(stand);
        return;
      }
      if (stand === 'weg') {
        ausgang = 'weg';
        fokusAbgeben();
        props.onWeg();
        return;
      }
      if (stand === undefined) {
        setFehler(changeUncertainMessage('umbenennen'));
        return;
      }
      // Der naechste Versuch traegt die frische lockVersion.
      await props.onNeuLaden();
      setFehler(notSavedAfterAbortMessage('umbenennen'));
    } finally {
      setPrueft(false);
      if (ausgang !== 'fertig' && ausgang !== 'weg') setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_, data) => { if (!data.open) schliessen(); }}>
      <DialogSurface>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void senden();
          }}
        >
          <DialogBody>
            <DialogTitle className={own.titel}>Sammlung umbenennen</DialogTitle>
            <DialogContent className={own.inhalt}>
              <NamensFeld
                id={NAME_ID}
                wert={name}
                onWert={(wert) => {
                  setName(wert);
                  setFeldFehler(undefined);
                }}
                fehler={feldFehler}
              />
              {fehler !== undefined ? <Meldung message={fehler} alert /> : undefined}
            </DialogContent>
            <DialogActions className={own.aktionen}>
              <NebenKnopf onClick={schliessen}>
                Abbrechen
              </NebenKnopf>
              <Button type="submit" appearance="primary" disabledFocusable={busy}>
                {busy ? (prueft ? 'Wird geprüft …' : 'Wird gespeichert …') : 'Speichern'}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}

export function BeendenDialog(
  props: Zugang & {
    readonly sammlung: CollectionSummary;
    readonly onSchliessen: () => void;
    readonly onBeendet: () => void;
    readonly onWeg: () => void;
    readonly onNeuLaden: () => Promise<boolean>;
  },
): ReactElement {
  const own = useStyles();
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Nach einem Abbruch: der Knopf sagt, dass abgeglichen wird.
  const [prueft, setPrueft] = useState(false);
  const abgebrochen = useRef(false);
  // Ein Versuch blieb ohne Antwort und ohne Abgleich: ein spaeteres 404 heisst dann "schon beendet".
  const offenerVersuch = useRef(false);

  const schliessen = (): void => {
    abgebrochen.current = true;
    fokusAbgeben();
    props.onSchliessen();
  };

  const beenden = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setFehler(undefined);
    let fertig = false;
    try {
      const token = await props.accessToken();
      if (token === undefined) {
        props.onReauthenticate();
        return;
      }
      const result = await endCollection({
        baseUrl: props.apiBaseUrl,
        accessToken: token,
        collectionId: props.sammlung.collectionId,
        lockVersion: props.sammlung.lockVersion,
      });
      // Abgebrochen, aber schon beendet: nicht verschweigen -- weiter zur Uebersicht mit dem Satz.
      if (abgebrochen.current) {
        if (result.outcome === 'applied') props.onBeendet();
        return;
      }
      if (result.outcome === 'applied' || result.outcome === 'not-found') {
        fertig = true;
        fokusAbgeben();
        if (result.outcome === 'applied' || offenerVersuch.current) props.onBeendet();
        else props.onWeg();
        return;
      }
      if (result.outcome === 'conflict' && result.reasonCode === 'party_collection_outdated') {
        // Wie beim Umbenennen: ist sie schon beendet, hat ein frueherer Versuch gewirkt.
        if ((await abgleichen(props, props.sammlung.collectionId)) === 'weg') {
          fertig = true;
          fokusAbgeben();
          props.onBeendet();
          return;
        }
        setFehler((await props.onNeuLaden()) ? result.message : outdatedMessage('beenden', false));
        return;
      }
      setFehler('message' in result ? result.message : NICHT_GESPEICHERT);
    } catch {
      setPrueft(true);
      const stand = await abgleichen(props, props.sammlung.collectionId);
      if (abgebrochen.current) {
        if (stand === 'weg') props.onBeendet();
        return;
      }
      if (stand === 'weg') {
        fertig = true;
        fokusAbgeben();
        props.onBeendet();
        return;
      }
      if (stand === undefined) {
        offenerVersuch.current = true;
        setFehler(changeUncertainMessage('beenden'));
        return;
      }
      await props.onNeuLaden();
      setFehler(notSavedAfterAbortMessage('beenden'));
    } finally {
      setPrueft(false);
      if (!fertig) setBusy(false);
    }
  };

  return (
    <Dialog open modalType="alert" onOpenChange={(_, data) => { if (!data.open) schliessen(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle className={own.titel}>Sammlung ‚{props.sammlung.collectionName}‘ beenden?</DialogTitle>
          <DialogContent className={own.inhalt}>
            <Body1>
              Die Sammlung verschwindet aus deiner Liste. Die Kontakte selbst bleiben unverändert. <strong>Beenden lässt sich nicht zurücknehmen.</strong>
            </Body1>
            {fehler !== undefined ? <Meldung message={fehler} alert /> : undefined}
          </DialogContent>
          <DialogActions className={own.aktionen}>
            {/* Zuerst und damit beim Oeffnen fokussiert: der sichere Weg. */}
            <NebenKnopf onClick={schliessen}>
              Abbrechen
            </NebenKnopf>
            <Button appearance="primary" disabledFocusable={busy} onClick={() => void beenden()}>
              {busy ? (prueft ? 'Wird geprüft …' : 'Wird beendet …') : 'Beenden'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
